import { ErrorDisplay } from '@txt/core.ui/ErrorDisplay';
import { LoadingIndicator } from '@txt/core.ui/LoadingIndicator';
import { CancelablePromise, cancelable, isErrorCanceled } from '@txt/core.utils/promise';
import { shallowCompareArrays } from '@txt/core.utils/react';
import * as React from 'react';
import { useCustomCompareEffect, useLatest } from 'react-use';

type State<T> = {
  isLoading: boolean;
  data: T | null;
  error: any;
};

type Loader<T> = (prevData: T | null) => Promise<T>;
type Comparator<T> = (prev: T, next: T) => boolean;

export type Options = {
  /** when disabled, change of loadParams is ignored */
  skip?: boolean;
  onError?: (err: unknown) => void;
  customLoadingCmp?: React.ReactElement;
  /**
   * When `true`, the previous data is not cleared when we reload the data because the dependencies did change.
   * When `false`, the data stays invalid until the fetch method returns new data.
   * Defaults to true.
   */
  passOutdatedData?: boolean;
};

type ReturnHelpers<T> = {
  setData: (data: T | null) => void;
  renderWithData: RenderWithData<T>;
  isLoading: boolean;
  error: any;
  reload: () => CancelablePromise<T>;
  next: () => void;
};
type ReturnType<T> = readonly [T | null, React.ReactElement | null, ReturnHelpers<T>] &
  ReturnHelpers<T> & { data: T | null; loadOrErrCmp: React.ReactElement | null };

type OptionsWithCustomCompare<Deps extends Array<unknown>> = Options & {
  equalityFunction?: Comparator<Deps>;
};

type RenderCallback<T> = (data: T, helpers: Omit<ReturnHelpers<T>, 'renderWithData'>) => React.ReactNode;
export type RenderWithData<T> = (renderCallback: RenderCallback<T>) => React.ReactNode;

/**
 * Custom hook to fetch data.
 */
export function useLoadData<T, D extends Array<unknown>>(
  loader: Loader<T>,
  dependencies: [...D],
  options?: OptionsWithCustomCompare<D>,
): ReturnType<T>;
export function useLoadData<T>(loader: Loader<T>, dependencies: Array<unknown>, options?: Options): ReturnType<T>;
export function useLoadData<T>(
  loader: Loader<T>,
  dependencies: Array<T>,
  options: Options | OptionsWithCustomCompare<Array<unknown>> = {},
): ReturnType<T> {
  const { skip = false, passOutdatedData = true } = options;
  const equalityFunction = ('equalityFunction' in options && options.equalityFunction) || shallowCompareArrays;

  function compare(
    [skipPrev, ...prev]: readonly [{ skip: boolean }, ...Array<unknown>],
    [skipNext, ...next]: readonly [{ skip: boolean }, ...Array<unknown>],
  ) {
    if (skipPrev.skip !== skipNext.skip) {
      return false;
    }

    return equalityFunction(prev, next);
  }

  const [state, setState] = React.useState<State<T>>({
    isLoading: !skip,
    data: null,
    error: null,
  });

  const latestLoader = useLatest(loader);
  const latestData = useLatest(state.data);

  const fetchData = React.useCallback((reload: boolean) => {
    setState((currentState) =>
      currentState.isLoading === true && currentState.error === null
        ? currentState // skips the state update if not needed
        : {
            ...currentState,
            isLoading: true,
            error: null,
            data: passOutdatedData || !reload ? currentState.data : null,
          },
    );

    const prevData = reload ? null : latestData.current;
    const loadPromise = cancelable(latestLoader.current(prevData));

    loadPromise
      .then((data) => {
        setState({
          isLoading: false,
          data,
          error: null,
        });
      })
      .catch((error) => {
        if (!isErrorCanceled(error)) {
          options.onError?.(error);
          setState((currentState) => ({
            ...currentState,
            isLoading: false,
            error,
          }));
        }
      });

    return loadPromise;
  }, []);

  // imperatively trigger reload of the fetch function
  const reload = React.useCallback(() => {
    return fetchData(true);
  }, []);
  // triggers the fetch function and provides the previous data
  // can be used to implement "load more" behavior
  const next = React.useCallback(() => {
    fetchData(false);
  }, []);

  useCustomCompareEffect(
    () => {
      if (skip) {
        return; // noop
      }

      const promise = fetchData(true);

      return () => promise.cancel();
    },
    // `useCustomCompareEffect(...)` requires you to at least provide a non-primitive value in the
    // dependency array. Nesting { skip } into an object fixes this. This won't result in
    // unnecessary rerenders because skip is handled specifically in the compare function on
    // rule 30. All is good.
    [{ skip }, ...dependencies] as const,
    // the types of react-use say it is (next, prev) but it is actually (prev, next)
    (prev, next) => compare(next, prev),
  );

  const { isLoading, data, error } = state;

  const loadOrErrCmp = getLoadingOrErrCmp(state, options.customLoadingCmp);

  const setData = React.useCallback((data: T | null) => setState((s) => ({ ...s, data })), []);

  const helpers = { isLoading, error, setData, reload, next };
  const renderWithData = (renderCallback: RenderCallback<T>): React.ReactNode => {
    if (loadOrErrCmp) {
      return loadOrErrCmp;
    }
    return renderCallback(data as T, helpers);
  };

  return Object.assign([data, loadOrErrCmp, { ...helpers, renderWithData }] as const, {
    data,
    loadOrErrCmp,
    ...helpers,
    renderWithData,
  });
}

export function getLoadingOrErrCmp({ isLoading, error }: State<unknown>, customLoadingCmp?: React.ReactElement) {
  if (isLoading) {
    return customLoadingCmp ? customLoadingCmp : <LoadingIndicator />;
  }

  if (error) {
    return <ErrorDisplay>{error}</ErrorDisplay>;
  }

  return null;
}
